// Angular
import { Injectable } from '@angular/core';

// Helpers
import { Base64 } from '../../lib/helpers/utils';

// Rxjs
import { Observable, of, throwError } from 'rxjs';

// Services
import { ApiService } from '../api/api.service';
import { SoledadCartService } from './cart.service';

// Models
import { Checkout, CheckoutHelper, CheckoutStatus } from '../../models/b2b/checkout';
import { CheckoutItem } from '../../models/b2b/checkoutItem';
import { CheckoutConfirmedHelper, CheckoutConfirmed } from '../../models/b2b/checkoutConfirmed';
import { User } from '../../models/b2b/user';
import { UserGenericHelper } from '../../models/b2b/userGeneric';
import { Payment, PaymentHelper } from '../../models/b2b/payment';
import { PaymentVerified, PaymentVerifiedHelper } from '../../models/b2b/paymentVerified';
import { CartPromo } from '../../models/b2b/cartPromo';
import { SaldoPromocionesHelper } from '../../models/b2b/saldoPromocionesItem';
import { map } from 'rxjs/operators';

@Injectable()
export class SoledadCheckoutService {
    constructor(
        private cartService: SoledadCartService,
        private api: ApiService
    ) {}

    /**
     * Make a api call to obtain the payment options
     * and updates checkout.user
     * Needs a user logued and set into checkout
     *
     * @memberOf SoledadCheckoutService
     */
    getPaymentOptions(checkout: Checkout): Observable<Payment[]> {
        if (!checkout.user) {
            return of([]);
        }

        const params = [];
        params['CLIENTE'] = checkout.user.clienteCodigo;

        return this.api.callSoledad('b2bNew.dbo.HTML_FormasPago', params).pipe(
            map((res) => {
                // console.log('HTML_FormasPago', res);

                if (res && res.result === 0 && res.value) {
                    return res.value.map((row) => PaymentHelper.transformFromApi(row));
                }

                return of([]);
            })
        );
    }

    private calculatePromoTotal(promos: CartPromo[]): number {
        let retorno = 0;

        for (const promoItem of promos) {
            retorno += promoItem.total > 0 ? promoItem.total : -1 * promoItem.total;
        }

        return retorno;
    }

    private getPortesTipoEntrega(items: CheckoutItem[]): number {
        let tipoEntregaQuantity = 0;
        for (const checkoutItem of items) {
            if (tipoEntregaQuantity <= 0 && checkoutItem.portesTipoEntrega > 0) {
                tipoEntregaQuantity = checkoutItem.portesTipoEntrega;
            }
        }
        return tipoEntregaQuantity;
    }

    private getTotalsRows(items: CheckoutItem[], user: User): string {
        let rows = [];

        for (const checkoutItem of items) {
            let row = [];

            row.push('ALMACEN=>' + checkoutItem.shipping.almacenCodigo);
            row.push('AGENCIA=>' + checkoutItem.shipping.agencia.trim());
            row.push('ARTICULO=>' + checkoutItem.cartItem.product.info.codigo);
            row.push('UNIDADES=>' + checkoutItem.quantity);

            if (user.modificarPrecio) {
                // row.push('PRECIO=>' + (checkoutItem.cartItem.totalLinea / checkoutItem.quantity) );
                row.push('PRECIO=>' + checkoutItem.totalLinea / checkoutItem.quantity);
            } else {
                // row.push('PRECIO=>' + (checkoutItem.cartItem.totalLineaSinDescuento / checkoutItem.quantity) );
                row.push('PRECIO=>' + checkoutItem.totalLineaSinDescuento / checkoutItem.quantity);
            }

            row.push('EXTERNO=>' + checkoutItem.parts);
            rows.push(row.join('&&'));
        }

        return Base64.encode(rows.join('##') + '##');
    }

    private fromApiToCheckout(checkout: Checkout, api: any): Checkout {
        checkout.importeArticulos = api['IMPORTE_ARTICULOS'];
        checkout.importeBruto = api['IMPORTE_BRUTO'];
        checkout.importeBrutoIncluidoDescuento = api['IMPORTE_BRUTO_INCLUIDO_DESCUENTO'];
        checkout.importeDescuento = api['IMPORTE_DESCUENTO'];
        checkout.importeIva = api['IMPORTE_IVA'];
        checkout.importeNfu = api['IMPORTE_NFU'];
        checkout.importePortes = api['IMPORTE_PORTES'];
        checkout.importePromos = api['IMPORTE_PROMOS'];
        checkout.importeTotal = api['IMPORTE_TOTAL'];
        checkout.importeSubtotal = checkout.importeBruto + checkout.importeIva + checkout.importePortes;
        checkout.puntos = api['PUNTOS'];
        checkout.textoImpuesto = api['TEXTO_IMPUESTO'];

        // Update validating
        checkout.status = CheckoutStatus.Validated;

        return checkout;
    }

    getTotalsParts(checkout: Checkout, user: User): Observable<Checkout> {
        if (!user) {
            return of(null);
        }
        const paymentTemp = CheckoutHelper.getSelectedPayment(checkout);
        const rows = this.getTotalsRows(checkout.items, user);
        const promoTotal = this.calculatePromoTotal(checkout.cart.promos);
        const portesTipoEntrega = this.getPortesTipoEntrega(checkout.items);
        const payment = paymentTemp ? paymentTemp : '';
        const paymentCode = payment ? payment.codigo : '';
        const params = [];

        if (!paymentCode) return of(null);

        params['ROWS'] = rows;
        params['CLIENTE'] = user.clienteCodigo;
        params['FORMA_PAGO'] = paymentCode;
        params['POR_AGENCIA'] = checkout.agency;
        params['IMPORTE_PROMO'] = promoTotal;

        params['PORTES_TIPO_ENTREGA'] = portesTipoEntrega;
        // saldo a utilizar
        const saldoUtilizar = checkout.cart.saldoPromociones
            ? SaldoPromocionesHelper.toAPI(checkout.cart.saldoPromociones)
            : null;
        if (saldoUtilizar !== null) {
            params['SALDO_UTILIZAR'] = saldoUtilizar;
        }
        // console.log('saldo', params['SALDO_UTILIZAR']);
        return this.api.callSoledad('b2bNew.dbo.HTML_ArticulosTotal_V3', params).pipe(
            map((res) => {
                if (res.value && res.value[0] && res.value[0].ERROR) {
                    throw 'Ocurrió un error obteniendo los totales: ' + res.value[0].ERROR;
                }
                if (res && res.result === 0 && res.value) {
                    checkout = this.fromApiToCheckout(checkout, res.value[0]);
                }

                return checkout;
            })
        );
    }

    /**
     * Make a api call to obtain totals of a checkout
     * and updates checkout.totals
     * Needs a user logued and set into checkout
     *
     * @memberOf SoledadCheckoutService
     */
    getTotals(checkout: Checkout, user: User): Observable<Checkout> {
        return this.getTotalsParts(checkout, user);
    }

    private hasDiscount(item: CheckoutItem): boolean {
        const ret =
            item.cartItem.product.rates.base.preDto1 > 0 ||
            item.cartItem.product.rates.base.preDto2 > 0 ||
            item.cartItem.product.rates.base.preDto3 > 0 ||
            item.cartItem.product.rates.base.preDto4 > 0 ||
            item.cartItem.product.rates.base.preDto5 > 0;

        return ret;
    }

    confirm(checkout: Checkout, user: User): Observable<CheckoutConfirmed> {
        // console.log('CONFIRM', {checkout}, {user});

        if (!user) {
            return throwError(() => new Error('No hay usuario.'));
        }

        if (!checkout.address) {
            return throwError(() => new Error('No hay dirección.'));
        }

        // Selected Address
        const selectedAddress = checkout.address;

        // Params
        const params = [];
        params['CLIENTE'] = user.clienteCodigo;
        params['ALMACEN'] = selectedAddress.almacenCodigo;
        params['DIRECCIONENVIO'] = selectedAddress.codigo;
        // params['DIRECCIONENVIO'] = checkout.addressIndex;
        params['AGENCIA'] = selectedAddress.agencia.trim();
        params['IDIOMA'] = this.api.environment.currentLang;
        params['OBSERVACIONES'] = checkout.annotations;
        params['PORAGENCIA'] = checkout.agency ? 1 : 0;
        params['NOMBRE_DIRENVIO'] = selectedAddress.nombre.trim();
        params['PEDIDOCLIENTE'] = ''; // Not active

        // Matricula y NumAutorizacion
        if (user.matriculaYAutorizacionObligatorias) {
            params['AUTORIZACION'] = checkout.numAutorizacion;
            params['MATRICULA'] = checkout.matricula;
        }

        // Promo
        if (checkout.idPromo.length > 0) {
            params['IDPROMO'] = checkout.idPromo;
        }

        // Replacement order
        if (checkout.activeReplacementOrder) {
            params['REPOSICION'] = true;
            params['REPOSICION_NOMBRE'] = checkout.replacementOrderName;
        }

        // Get delivery items
        const rows = [];
        const checkoutItems = checkout.items;

        for (const checkoutItem of checkoutItems) {
            const row = [];

            row.push('DESCRIPCION=>' + checkoutItem.cartItem.product.info.nombre.trim());
            row.push('CANTIDAD=>' + checkoutItem.shipping.cantidad);

            if (user.modificarPrecio || (!user.modificarPrecio && this.hasDiscount(checkoutItem))) {
                row.push('PRECIO=>' + checkoutItem.cartItem.precioSinDescuento);
                row.push('PRECIOMOSTRAR=>' + checkoutItem.cartItem.precioSinDescuento);
            } else {
                row.push('PRECIO=>' + checkoutItem.priceO);
                row.push('PRECIOMOSTRAR=>' + checkoutItem.priceO);
            }

            row.push('PROMOCION=>' + checkoutItem.cartItem.product.rates.base.prePrm);
            row.push('DTO1=>' + checkoutItem.cartItem.product.rates.base.preDto1);
            row.push('DTO2=>' + checkoutItem.cartItem.product.rates.base.preDto2);
            row.push('DTO3=>' + checkoutItem.cartItem.product.rates.base.preDto3);
            row.push('DTO4=>' + checkoutItem.cartItem.product.rates.base.preDto4);
            row.push('DTO5=>' + checkoutItem.cartItem.product.rates.base.preDto5);

            if (checkoutItem.parts) {
                const json = JSON.parse(checkoutItem.json);
                for (let key in json) {
                    json[key].cantidad = checkoutItem.quantity;
                }

                row.push('ARTICULO=>' + checkoutItem.product_id);
                row.push('TIEMPO=>' + checkoutItem.shipping.tiempo2);
                row.push('ALMACEN=>' + checkoutItem.shipping.almacenCodigo);
                row.push('UNIDADMEDIDA=>' + '');
                row.push('EXTERNO=>' + true);
                row.push('JSON_EXTERNO=>' + JSON.stringify(json));
            } else {
                row.push('ARTICULO=>' + checkoutItem.cartItem.product.info.codigo);
                row.push('TIEMPO=>' + checkoutItem.cartItem.product.horas);
                row.push('ALMACEN=>' + selectedAddress.almacenCodigo);
                row.push('UNIDADMEDIDA=>' + checkoutItem.cartItem.product.info.unidadMedida);
                row.push('EXTERNO=>' + false);
            }

            const shippingSelected = checkoutItem.shippingOptionSelected;
            if (shippingSelected && shippingSelected.id) {
                row.push('TIPOENTREGA=>' + shippingSelected.id);
            }

            rows.push(row.join('&&'));
        }

        // Set delivery rows
        params['ROWS'] = Base64.encode(rows.join('##') + '##');

        // Check payment method
        const selectedPaymentMethod = CheckoutHelper.getSelectedPayment(checkout);
        params['PASARELA'] = selectedPaymentMethod.pasarela ? selectedPaymentMethod.pasarela : '';

        // If set to 1 the service doesnt return URL_PASARELA for transfer payment method
        params['ACEPTAR_TRANSFERENCIA'] = 1;

        // Webtrekk EID for conversion seek
        if (checkout.eid !== '') {
            params['WEBTREKK_ID'] = checkout.eid;
        }

        // User
        if (user.clienteGenerico) {
            const userGeneric = UserGenericHelper.fromUser(user);

            // If we have different delivery address
            if (userGeneric.same_address_shipping) {
                params['de_usarOtraDireccionEnvio'] = '1';

                if (userGeneric.shipping_name) {
                    params['de_nombre'] = userGeneric.shipping_name;
                }
                if (userGeneric.shipping_lastname) {
                    params['de_apellidos'] = userGeneric.shipping_lastname;
                }
                if (userGeneric.shipping_address) {
                    params['de_direccion'] = userGeneric.shipping_address;
                }
                if (userGeneric.shipping_city) {
                    params['de_municipio'] = userGeneric.shipping_city;
                }
                if (userGeneric.shipping_phone) {
                    params['de_telefono'] = userGeneric.shipping_phone;
                }

                if (userGeneric.shipping_zip_code) {
                    let cp = userGeneric.shipping_zip_code;
                    cp = userGeneric.shipping_zip_code + '';

                    // ZipCode lpad
                    while (cp.length < 5) {
                        cp = '0' + cp;
                    }

                    params['de_cp'] = cp;
                    params['de_provincia'] = cp.substr(0, 2);
                }
            }
        }

        // saldo a utilizar
        params['SALDO_UTILIZAR'] = checkout.cart.saldoPromociones
            ? SaldoPromocionesHelper.toAPI(checkout.cart.saldoPromociones)
            : null;
        // console.log('params', params);
        // debugger;

        const self = this;

        // TEST NO PAY ***********************************
        // let confirmed = CheckoutConfirmedHelper.create();
        // confirmed.error = 'ERROR TEST PAGO';
        // confirmed.mensaje = 'ERROR TEST PAGO';
        // console.log('CONFIRM PARAMS', {params});

        // return Observable.of(confirmed);
        // ***********************************************

        // TEST PAY OK ***********************************
        // console.log('params', params);
        // let confirmed = CheckoutConfirmedHelper.create();
        // confirmed.resultado = 0;
        // confirmed.pagoId = '1234567890';
        // // // confirmed.importeTotal = 200;
        // // // confirmed.pasarelaEntidad = '11683';
        // // // confirmed.pasarelaReferencia = '37000086'
        // // // ret.error = 'retorno de prueba';
        // confirmed.mensaje = 'Pago realizado correctamente.';
        // return of(confirmed);
        // ***********************************************

        return this.api.callSoledad('b2bNew.dbo.HTML_PedidosGrabar_NOSESION', params).pipe(
            map((res) => {
                if (
                    !res ||
                    res.result !== 0 ||
                    !res.value ||
                    res.value.length === 0 ||
                    (res.value.length && res.value[0].ERROR)
                ) {
                    if (res && res.value && res.value.length && res.value[0].ERROR) {
                        console.error(res.value[0].ERROR);
                        throw 'Ocurrió un error al grabar el pedido: ' + res.value[0].ERROR;
                    }

                    return;
                }

                // Checkout Result
                const result = res.value[0];

                // Confirmed
                const confirmed = CheckoutConfirmedHelper.transformFromApi(result);

                // Annotations
                confirmed.annotations = checkout.annotations;

                // Totals
                confirmed.importeArticulos = checkout.importeArticulos;
                confirmed.importeBruto = checkout.importeBruto;
                confirmed.importeBrutoIncluidoDescuento = checkout.importeBrutoIncluidoDescuento;
                confirmed.importeDescuento = checkout.importeDescuento;
                confirmed.importeIva = checkout.importeIva;
                confirmed.importeNfu = checkout.importeNfu;
                //confirmed.importePortes = checkout.importePortes;
                confirmed.importePromos = checkout.importePromos;
                //confirmed.importeTotal = checkout.importeTotal;
                confirmed.puntos = checkout.puntos;
                confirmed.textoImpuesto = checkout.textoImpuesto;
                confirmed.annotations = checkout.annotations;

                // Send
                confirmed.sendNombre = selectedAddress.nombre;
                confirmed.sendDireccion = selectedAddress.direccion;
                confirmed.sendCp = selectedAddress.cp;
                confirmed.sendMunicipio = selectedAddress.municipio;
                confirmed.sendProvincia = selectedAddress.provincia;
                confirmed.sendPais = selectedAddress.pais;

                // Bill
                confirmed.billNombre = checkout.user.clienteNombre;
                confirmed.billNif = checkout.user.clienteNif;
                confirmed.billDireccion = checkout.user.clienteDireccion;
                confirmed.billCp = checkout.user.clienteCp;
                confirmed.billMunicipio = checkout.user.clienteMunicipio;
                confirmed.billPais = checkout.user.clientePais;

                // Payment
                confirmed.payment = CheckoutHelper.getSelectedPayment(checkout).nombre;

                // Items
                for (const item of checkout.items) {
                    // const shipping = item.shippingOptionSelected;
                    const shipping = item.shipping;
                    const obj = {
                        product: item.cartItem.product.info.nombre,
                        quantity: item.quantity,
                        price: item.priceO,
                        // send: shipping.descripcion + (shipping.descuento > 0 ? ` (Dto ${shipping.descuento}%)` : '')
                        send: shipping.descripcion
                    };
                    confirmed.items.push(obj);
                }

                // Save localStorage
                sessionStorage.setItem('lastCheckout', Base64.encode(JSON.stringify(confirmed)));

                return confirmed;
            })
        );
    }

    /**
     * Clear checkout and cart
     */
    clear(checkout: Checkout) {
        // Reset Checkout
        CheckoutHelper.clear(checkout);

        // Reset Cart Service
        checkout.cart = this.cartService.clear(checkout.user.clienteCodigo);
    }

    /**
     * Get LastCheckout
     *
     * @returns {CheckoutConfirmed}
     */
    getLastCheckout(): CheckoutConfirmed {
        const jsonLast = sessionStorage.getItem('lastCheckout');
        if (!jsonLast) {
            return null;
        }
        return JSON.parse(Base64.decode(jsonLast));
    }

    setLastPaymentMethod(user: User, value: string): void {
        localStorage.setItem(user.clienteCodigo + 'lpm', value + '');
    }

    getLastPaymentMethod(user: User): string {
        return localStorage.getItem(user.clienteCodigo + 'lpm') || null;
    }

    setCondicionesAceptadas(checked: boolean): Observable<boolean> {
        // console.log('setCondicionesAceptadas');

        const params = [];
        params['CONDICIONESACEPTADAS'] = checked;

        // console.log({params});

        return this.api.callSoledad('b2bNew.dbo.HTML_ConfiguracionGrabar', params).pipe(
            map(
                (res) => {
                    // console.log('b2bNew.dbo.HTML_ConfiguracionGrabar', {res});
                    if (res && res.result === 0 && res.value && !res.value[0].ERROR) {
                        return true;
                    } else {
                        return false;
                    }
                },
                (error) => {
                    console.log({ error });
                }
            )
        );
    }

    getPaymentVerification(code: number | string): Observable<PaymentVerified> {
        const params = [];
        params['PAGO_ID'] = code;

        // console.log({params});

        return this.api.callSoledad('b2bNew.dbo.HTML_PedidosComprobarPago', params).pipe(
            map((data) => {
                // console.log('HTML_PedidosComprobarPago', {data});
                if (data && data.result === 0 && data.value) {
                    const paymentVerified = PaymentVerifiedHelper.transformFromApi(data.value[0]);
                    return paymentVerified;
                }
            })
        );
    }
}
